import { useEffect, useState } from 'react';
import { TabBar } from 'antd-mobile';
import { AppOutline, UnorderedListOutline, UserOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { supabase } from '@ubus/shared';
import { useAuth } from '../hooks/useAuth';
import { useDriverReminderUnread } from '../hooks/useDriverReminderUnread';
import SOSButton from './SOSButton';
import GPSManager from '../utils/GPSManager';

/** Keeps post-arrival GPS alive when driver navigates away from task detail. */
const backgroundGpsRef: { current: GPSManager | null } = { current: null };

function DriverLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { driverId } = useAuth();
  const reminderUnread = useDriverReminderUnread();
  const [activeTripId, setActiveTripId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!driverId) return;

    let cancelled = false;
    (async () => {
      const restored = await GPSManager.restoreIfPending(driverId);
      if (!cancelled && restored) {
        backgroundGpsRef.current = restored;
        return;
      }

      const shift = await GPSManager.startShiftReporting(driverId);
      if (!cancelled && shift) {
        backgroundGpsRef.current = shift;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [driverId]);

  useEffect(() => {
    const path = location.pathname;
    const match = path.match(/\/task\/([^/]+)/);
    if (match) {
      setActiveTripId(match[1]);
      return;
    }

    if (!driverId) {
      setActiveTripId(undefined);
      return;
    }

    let cancelled = false;
    (async () => {
      const { data: inProgress } = await supabase
        .from('trips')
        .select('id')
        .eq('driver_id', driverId)
        .eq('status', 'in_progress')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (inProgress?.id) {
        if (!cancelled) setActiveTripId(inProgress.id);
        return;
      }

      const { data: dispatched } = await supabase
        .from('trips')
        .select('id')
        .eq('driver_id', driverId)
        .eq('status', 'dispatched')
        .order('pickup_time', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (!cancelled) setActiveTripId(dispatched?.id);
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, driverId]);

  const tabs = [
    { key: '/', title: t('nav.tasks'), icon: <AppOutline /> },
    { key: '/my-tasks', title: t('nav.myTasks'), icon: <UnorderedListOutline /> },
    {
      key: '/profile',
      title: t('nav.profile'),
      icon: <UserOutline />,
      badge: reminderUnread > 0 ? (reminderUnread > 99 ? '99+' : reminderUnread) : undefined,
    },
  ];

  const getActiveKey = () => {
    const path = location.pathname;
    if (path.includes('profile') || path.includes('sos-history') || path.includes('reminders')) {
      return '/profile';
    }
    if (path.includes('my-tasks')) return '/my-tasks';
    return '/';
  };

  const hideTabBar =
    location.pathname.includes('/task/') ||
    location.pathname.includes('/sos-history') ||
    location.pathname.includes('/reminders') ||
    location.pathname.includes('/inspection');

  return (
    <div className="driver-shell">
      <div className={`driver-content ${hideTabBar ? '' : 'driver-content--with-tabbar'}`}>
        <Outlet />
      </div>

      {!hideTabBar && <SOSButton tripId={activeTripId} />}

      {!hideTabBar && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: 'white' }}>
          <TabBar activeKey={getActiveKey()} onChange={(key) => navigate(key)}>
            {tabs.map((tab) => (
              <TabBar.Item
                key={tab.key}
                icon={tab.icon}
                title={tab.title}
                badge={'badge' in tab ? tab.badge : undefined}
              />
            ))}
          </TabBar>
        </div>
      )}
    </div>
  );
}

export default DriverLayout;
